// หน้าสรุปชีวิต (docs/PLAN.md ข้อ 7)

import { useState } from "react";
import { archetypeOf } from "../../engine/archetypes";
import type { AchievementDef } from "../../shared/achievements";
import { ancestorOf, canContinueAsChild, stagesOf, summarize } from "../../engine/life";
import { DEATH_CAUSE_TH, displayName, formatAge } from "../../engine/text";
import type { Ancestor, LifeState, MateKind } from "../../engine/types";
import type { SpeciesRange } from "../../geo/range";
import type { SpawnResult } from "../../shared/api";
import { GeoPanel } from "../components/GeoPanel";
import { ShareCardPanel } from "../components/ShareCardPanel";
import { SpeciesStatsPanel } from "../components/SpeciesStatsPanel";
import { Timeline } from "../components/Timeline";
import type { GeoState } from "../geo";
import { fmt, offspringLabel } from "../names";

interface Props {
  life: LifeState;
  spawn: SpawnResult;
  geo: GeoState;
  range: SpeciesRange | null;
  onNewLife: () => void;
  onContinue: () => void; // เล่นต่อเป็นลูก
  dexNote: string | null; // ได้ชนิดใหม่ลงสมุดสัตว์ หรือทำสถิติใหม่
  onDex: () => void;
  unlocked: AchievementDef[]; // ความสำเร็จที่เพิ่งได้จากชีวิตนี้
  sent: Promise<void>; // ส่งชีวิตนี้ไปรวมเป็นสถิติของผู้เล่นทั้งหมดแล้ว
  onAchievements: () => void;
}

const MATE_TH: Record<MateKind, string> = { strong: "คู่ที่แข็งแรงที่สุด", provider: "คู่ที่หาอาหารเก่ง", near: "คู่ที่อยู่ใกล้ที่สุด" };

/** หนึ่งรุ่นในสายตระกูล */
function Generation({ n, a, stageName, me }: { n: number; a: Ancestor; stageName: string; me?: boolean }) {
  return (
    <li>
      <b>
        รุ่นที่ {n}
        {me ? " (คุณ)" : ""}
      </b>{" "}
      <span className="small">
        อายุ {formatAge(a.ageDays)} · {stageName} · ลูก {fmt(a.offspring)} · {a.cause ? DEATH_CAUSE_TH[a.cause] : "-"}
        {a.killer ? ` (${a.killer})` : ""}
        {a.mate ? ` · เลือก${MATE_TH[a.mate]}` : ""}
      </span>
    </li>
  );
}

export function Summary({ life, spawn, geo, range, onNewLife, onContinue, dexNote, onDex, unlocked, sent, onAchievements }: Props) {
  const s = summarize(life);
  const stage = stagesOf(life.species).find((x) => x.id === s.stageId)!;
  const [copied, setCopied] = useState<string | null>(null);
  const lineage = [...(life.lineage?.ancestors ?? []), ancestorOf(life)];
  const stageName = (id: string) => stagesOf(life.species).find((x) => x.id === id)?.nameTh ?? id;
  const shareUrl = `${location.origin}/?species=${life.species.id}&seed=${encodeURIComponent(life.seed)}`;

  async function share() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied("คัดลอกลิงก์แล้ว ส่งให้เพื่อนเกิดเป็นตัวเดียวกันได้เลย");
    } catch {
      setCopied(shareUrl);
    }
  }

  return (
    <main className="screen">
      <section className="card">
        <p className="muted small">จบชีวิตของ</p>
        <h1 className="species-name">{displayName(life.species)}</h1>
        <p className="sci">{life.species.sci}</p>
      </section>

      <section className="summary-big">
        <div className="card">
          <span className="muted small">อายุ</span>
          <b>{formatAge(s.ageDays)}</b>
        </div>
        <div className="card">
          <span className="muted small">{offspringLabel(life.flags)}</span>
          <b>{fmt(s.offspring)}</b>
        </div>
        <div className="card">
          <span className="muted small">ช่วงวัยสุดท้าย</span>
          <b>{stage.nameTh}</b>
        </div>
        <div className="card">
          <span className="muted small">สาเหตุ</span>
          <b style={{ fontSize: "1.05rem" }}>{s.cause ? DEATH_CAUSE_TH[s.cause] : "-"}</b>
          {life.killer && (
            <span className="small">
              {life.killer.nameTh ?? life.killer.sci} <span className="sci muted">{life.killer.sci}</span>
            </span>
          )}
        </div>
      </section>

      {dexNote && (
        <p className="notice dex-note">
          {dexNote}{" "}
          <button className="link" onClick={onDex}>
            เปิดสมุดสัตว์
          </button>
        </p>
      )}

      {unlocked.length > 0 && (
        <section className="card achievement got">
          <p className="small muted">ได้ความสำเร็จใหม่ {unlocked.length} อย่าง</p>
          <ul className="unlocked">
            {unlocked.map((a) => (
              <li key={a.id}>
                <b>{a.nameTh}</b> <span className="small muted">{a.description}</span>
              </li>
            ))}
          </ul>
          <button className="link small" onClick={onAchievements}>
            ดูความสำเร็จทั้งหมด
          </button>
        </section>
      )}

      <SpeciesStatsPanel life={life} sent={sent} />

      {!s.reachedAdult && <p className="notice">{archetypeOf(life.species).earlyDeathFact}</p>}

      <GeoPanel geo={geo} card={spawn} range={range} track={life.geo ?? null} mode="life" life={life} />

      {lineage.length > 1 && (
        <section className="card lineage">
          <h2 style={{ fontSize: "1.05rem", marginBottom: 8 }}>สายตระกูล {lineage.length} รุ่น</h2>
          <ol>
            {lineage.map((a, i) => (
              <Generation key={a.seed} n={(life.lineage?.generation ?? 1) - lineage.length + i + 1} a={a} stageName={stageName(a.stageId)} me={i === lineage.length - 1} />
            ))}
          </ol>
        </section>
      )}

      <div className="actions">
        {canContinueAsChild(life) && (
          <button className="btn primary block" onClick={onContinue}>
            เล่นต่อเป็นลูกของคุณ
          </button>
        )}
        <button className={`btn block${canContinueAsChild(life) ? "" : " primary"}`} onClick={onNewLife}>
          เกิดใหม่เป็นสัตว์อื่น
        </button>
        <ShareCardPanel life={life} geo={geo} range={range} achievements={unlocked.map((a) => a.nameTh)} shareUrl={shareUrl} />
        <button className="btn block" onClick={share}>
          คัดลอกลิงก์ให้เพื่อนเกิดเป็นตัวเดียวกัน
        </button>
        {copied && <p className="small muted" style={{ wordBreak: "break-all" }}>{copied}</p>}
      </div>

      <section className="card">
        <h2 style={{ fontSize: "1.05rem", marginBottom: 8 }}>เส้นทางชีวิต</h2>
        <Timeline log={life.log} />
      </section>

      <p className="small">
        <a href="/animal">ดูรายชื่อสัตว์ในฐานข้อมูล</a>
      </p>
    </main>
  );
}
